import { useRef, useState } from "react";
import { BELGE_TURU_ADI, SATIN_ALMA_ALANLARI, hedefEsit, satinAlmaEtiketi, type GelenHedef, type SatinAlmaKartSatiri, type SatinAlmaKarti } from "@oasis/domain";
import { Band, Buton, Etiket } from "@/components/parcalar";
import { AltBaslik, Nokta, UyariSatiri } from "@/components/belge-okuma-bolumu";
import { api } from "@/lib/api";
import {
  OKUMA_UCU,
  SATIN_ALMA_UCU,
  okumaDugmesi,
  okumaEylemleri,
  satinAlmaCinsMetni,
  satinAlmaDurumMetni,
  satinAlmaEkleyen,
  satinAlmaFaturaMi,
  satinAlmaFiyatMetni,
  satinAlmaFormu,
  satinAlmaKarsilastirmaSatirlari,
  satinAlmaPoKalirMetni,
  satinAlmaSatirMetni,
  satinAlmaSayfalari,
  satinAlmaTarihMetni,
} from "@/lib/belgeler";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { dosyaYukle, tarihSaat } from "@/lib/veri";

/**
 * D-074 Faz 4 · PR 23: Documents kartının PO "Document reading" bölümü (prototip belgeler--po-okuma, kart 1-4; `UygunlukOkumaBolumu` iskeleti). Okuma yalnız eşlemenin, kuralların,
 * "Use the reading"in ve "Add to package"in kullandığı değerleri gösterir (sözleşme §15): başlıkta 4, satır başına 4 değer. Fiyat tutarları, toplamlar, vergi, adres blokları, satır
 * teslim tarihi, alıcının malzeme no'su, satıcı teklifi, şartlar ve imzalar okunmaz; PN'i stok kataloğunda olmayan satırlar okunmaz. Commercial invoice yalnız tanınır (satır tablosu
 * çizilmez). OASIS her satıra aynı PN'li ve bu PO'yu zaten taşıyan paketi önerir. "Use this place" kartın bugünkü tür+yer kaydını çağırır; "Use the reading" yalnız dosyalama formunu
 * doldurur (sunucuya yazmaz). Dosyalamadan sonra PO öteki paketlere "Add to package" ile tek tek eklenir; hiçbir paket açılmaz ya da değişmez (PO no'su dahil). `salt` kapanmış satır
 * içindir: form ve karar düğmeleri yok; "Add to package" sunucunun eylemiyle çizilir. Okuma excel'inin adı kartta yoktur. Bölüm yalnız satırı TAM görenlere (PM, sistem yöneticisi) gelir.
 */

const ETIKET_SINIFI = "text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3";
const TABLO_BASI = "px-2 py-1.5 text-left text-xs font-medium text-ink-3";
const TABLO_HUCRESI = "px-2 py-1.5 align-top [overflow-wrap:anywhere]";

const aralik = (s: { bas: number; son: number }) => (s.bas === s.son ? String(s.bas) : `${s.bas}-${s.son}`);
const durumTonu = (d: "kisi" | "oneri" | "yok") => (d === "kisi" ? "ok" : d === "oneri" ? "info" : "neutral");

export interface SatinAlmaFormYardimi {
  /** "Use the reading": dosyalama formunun alanları (yalnız kesin PO no) */
  kullan: (alanlar: Record<string, string>) => void;
  /** "Use the reading": Pages alanı (PO PDF'in bir kısmıysa) */
  sayfalar?: (s: string) => void;
  /** "Use this place": bugünkü tür+yer kaydı */
  yerKaydet: (h: GelenHedef) => Promise<void>;
}

export function SatinAlmaOkumaBolumu({ satirId, k, onYenile, salt, hedef, form }: { satirId: string; k: SatinAlmaKarti; onYenile: () => void; salt?: boolean; hedef: GelenHedef | null; form?: SatinAlmaFormYardimi }) {
  const [mesgul, setMesgul] = useState<string | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [degismedi, setDegismedi] = useState(false);
  const secici = useRef<HTMLInputElement>(null);
  const o = k.okuma;
  const ey = okumaEylemleri(k, salt);
  const yardim = salt ? undefined : form;

  const calistir = async (anahtar: string, f: () => Promise<unknown>) => {
    setMesgul(anahtar);
    setHata(null);
    setDegismedi(false);
    try {
      await f();
      onYenile();
    } catch (e) {
      setHata(e instanceof Error ? e.message : cevir("Error"));
    } finally {
      setMesgul(null);
    }
  };

  const sayfaLinki = (sayfa: number | null) =>
    o && sayfa !== null ? <> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(o.dokumanId, { sayfa })} target="_blank" rel="noreferrer">p.{sayfa}</a></> : null;
  const fatura = satinAlmaFaturaMi(k);
  const karsilastirmaSatirlari = satinAlmaKarsilastirmaSatirlari(k);
  const toplamUyari = (k.karsilastirma?.uyarilar.length ?? 0) + k.satirlar.reduce((t, c) => t + c.uyarilar.length, 0);
  const sayfalar = satinAlmaSayfalari(k);
  const sariGri = toplamUyari > 0 && [...k.satirlar.flatMap((c) => c.uyarilar), ...(k.karsilastirma?.uyarilar ?? [])].every((u) => u.renk !== "kirmizi");
  const dosyalanmis = k.satirlar.some((c) => c.durum === "pakette" || c.durum === "eklenebilir");
  const faturaAdi = cevir(BELGE_TURU_ADI.stok_paket.fatura ?? "Commercial invoice");

  const satirHucresi = (c: SatinAlmaKartSatiri) => <span className="font-mono">{cevir("{0} · p.{1}", c.sira, c.sayfa ?? "—")}</span>;
  const belgeHucresi = (c: SatinAlmaKartSatiri) => {
    const suphe = c.degerler.some((d) => d.kesinlik === "suphe");
    return (
      <span className="inline-flex items-center gap-1.5"><Nokta k={suphe ? "suphe" : "kesin"} /><span>{satinAlmaSatirMetni(c)}</span>{sayfaLinki(c.sayfa)}</span>
    );
  };
  const paketHucresi = (c: SatinAlmaKartSatiri) => {
    const p = c.yer.paket;
    if (!p || c.yer.durum === "yok") return <span className="text-ink-3">—</span>;
    return (
      <>
        <span className="mr-1.5">
          {[p.pkg, p.pn, p.iz ? cevir("heat / lot / SN {0}", p.iz) : cevir("no heat / lot / SN"), p.poNo ? cevir("PO {0}", p.poNo) : cevir("no PO")].join(" · ")}
        </span>
        <Etiket tone={durumTonu(c.yer.durum)}>{c.yer.durum === "kisi" ? cevir("your choice") : cevir("proposed")}</Etiket>
        {yardim && c.yer.durum === "oneri" && c.yer.yer && !hedefEsit(c.yer.yer, hedef) && (
          <Buton kucuk className="ml-1 mt-1" disabled={mesgul !== null} mesgul={mesgul === `yer-${c.sira}`} onClick={() => calistir(`yer-${c.sira}`, () => yardim.yerKaydet(c.yer.yer!))}>{cevir("Use this place")}</Buton>
        )}
      </>
    );
  };
  const sonucHucresi = (c: SatinAlmaKartSatiri) => {
    const ekleyen = satinAlmaEkleyen(c);
    return (
      <>
        {c.durum === "eklenebilir" && k.eylemler.ekle
          ? (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === `ekle-${c.sira}`} onClick={() => calistir(`ekle-${c.sira}`, () => api.post(SATIN_ALMA_UCU.ekle(satirId), { sira: c.sira }))}>
              {cevir("Add to package")}
            </Buton>
          )
          : satinAlmaDurumMetni(c, hedef)}
        {ekleyen && <span className="block text-xs text-ink-3">{cevir("Added by {0} on {1}", ekleyen.kim, tarihSaat(ekleyen.ts))}</span>}
      </>
    );
  };
  const degerMetni = (alan: string) => {
    const d = k.degerler.find((x) => x.alan === alan);
    if (!d?.deger) return null;
    if (alan === "belge_cinsi") return satinAlmaCinsMetni(d.deger);
    if (alan === "fiyat_var") return satinAlmaFiyatMetni(d.deger);
    if (alan === "siparis_tarihi") return satinAlmaTarihMetni(d);
    return d.deger;
  };

  return (
    <div data-bolum="satin-alma-okuma">
      <h3 className={cn("mb-1.5", ETIKET_SINIFI)}>{cevir("Document reading")}</h3>
      {k.is.durum === "sirada" && <p className="mt-1 text-sm">{cevir("In the reading queue · place {0}", k.is.siraNo ?? "—")}</p>}
      {k.is.durum === "donusturuluyor" && <p className="mt-1 text-sm">{cevir("The converter is reading this file.")}</p>}
      {k.is.durum === "hata" && (
        <Band tip="not" className="mt-1.5">
          {cevir("Could not read: {0}", cevir(k.is.hata ?? ""))}
          {k.is.hataTs && <span className="mt-0.5 block text-xs">{tarihSaat(k.is.hataTs)}</span>}
        </Band>
      )}
      {!o && !k.is.durum && !salt && <p className="mt-1 text-sm text-ink-2">{cevir("OASIS reads the PO for you to check. Prices are not read; nothing is filed or changed.")}</p>}
      {k.engel && !salt && <Band tip="not" className="mt-1.5">{cevir(k.engel)}</Band>}

      {o && (
        <>
          <p className="text-xs text-ink-3">
            {[
              o.sayfalar ? cevir("Purchase order p.{0}", aralik(o.sayfalar)) : null,
              k.kod ? cevir("Reading job {0}", k.kod) : null,
              cevir("{0} lines read", o.satirSayisi),
            ].filter(Boolean).join(" · ")}
          </p>
          <p className="text-xs text-ink-3">
            {o.kaynak.tur === "donusturucu"
              ? cevir("Read by the converter on {0} · engine {1}", tarihSaat(o.kaynak.ts), o.motor)
              : cevir("Reading excel uploaded by {0} on {1} · engine {2}", o.kaynak.ad, tarihSaat(o.kaynak.ts), o.motor)}
          </p>

          <ul className="mt-2 space-y-0.5 text-sm" data-bolum="satin-alma-baslik">
            {SATIN_ALMA_ALANLARI.filter((a) => !fatura || a.alan === "belge_cinsi").map((a) => {
              const d = k.degerler.find((x) => x.alan === a.alan);
              const deger = degerMetni(a.alan);
              return (
                <li key={a.alan} className="flex gap-2">
                  <Nokta k={d?.kesinlik ?? "bos"} />
                  <span className="w-[10rem] shrink-0 text-ink-2">{cevir(satinAlmaEtiketi(a.alan))}</span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {deger ?? <span className="text-ink-3">{cevir("not read")}</span>}
                    {sayfaLinki(d?.sayfa ?? null)}
                  </span>
                </li>
              );
            })}
          </ul>

          {fatura
            ? <p className="mt-2 text-sm text-ink-2">{cevir("A commercial invoice is only recognised; nothing else on it is read.")}</p>
            : (
              <>
                <AltBaslik>{cevir("Lines of the PO")}</AltBaslik>
                <div className="overflow-x-auto rounded-md border border-line">
                  <table className="w-full text-sm" data-bolum="bl-kars">
                    <thead><tr className="border-b border-line">
                      <th className={TABLO_BASI}>{cevir("Line")}</th>
                      <th className={TABLO_BASI}>{cevir("On the PO")}</th>
                      <th className={TABLO_BASI}>{cevir("Stock package")}</th>
                      <th className={TABLO_BASI}>{cevir("Result")}</th>
                    </tr></thead>
                    <tbody className="divide-y divide-line">
                      {k.satirlar.map((c) => (
                        <tr key={c.sira} data-satir={c.sira}>
                          <td className={TABLO_HUCRESI}>{satirHucresi(c)}</td>
                          <td className={TABLO_HUCRESI}>{belgeHucresi(c)}</td>
                          <td className={TABLO_HUCRESI}>{paketHucresi(c)}</td>
                          <td className={TABLO_HUCRESI}>{sonucHucresi(c)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-1 text-xs text-ink-3">{cevir("OASIS proposes the package of the same part number that already carries this PO: its PO no., a PO document of this number or its read packing list. Nothing is filed by itself.")}</p>
                {(k.eylemler.ekle || dosyalanmis) && (
                  <p className="mt-1 text-xs text-ink-3">
                    {cevir("“Add to package” files this PDF on the package of that line too (PO, Doc no. {0}). One line at a time; a package or a document already in Registers does not change. A line without a proposed package is filed on Registers by hand.", satinAlmaFormu(k).belgeNo || "—")}
                  </p>
                )}
              </>
            )}

          {yardim && !fatura && (
            <div className="mt-2 flex flex-wrap items-center gap-2" data-bolum="okumayi-kullan">
              <Buton kucuk disabled={mesgul !== null} onClick={() => {
                yardim.kullan(satinAlmaFormu(k));
                if (sayfalar && yardim.sayfalar) yardim.sayfalar(sayfalar);
              }}>{cevir("Use the reading")}</Buton>
              <span className="w-full text-xs text-ink-3">
                {cevir("“Use the reading” fills Doc no. with the certain PO number and Pages with the pages of the PO when it is part of the PDF; an uncertain or empty value stays empty for you to type. Nothing is filed until you confirm.")}
              </span>
            </div>
          )}
          {yardim && (fatura || sariGri) && (
            <Band tip="not" className="mt-2">
              <span className="block font-medium">{cevir("Before you file")}</span>
              {fatura && <span className="block">{cevir("The document reads as {0}. Change “What is this file?” to {0} and file it by hand, or file it as reference.", faturaAdi)}</span>}
              {!fatura && sariGri && <span className="block">{cevir("Amber and grey alerts do not stop filing; they stay with the reading.")}</span>}
            </Band>
          )}
          <p className="mt-1 text-xs text-ink-3">
            {cevir("Not read: prices, totals and taxes, the address blocks (a number there is not a PO), the delivery date and the buyer’s material number of the lines, the vendor quote, terms and signatures. Lines whose part number is not in the stock catalogue are not read.")}
          </p>
          <p className="mt-1 text-xs text-ink-3">{cevir("Visible to the PM and the System administrator only. The PO no. of a package does not change.")}</p>
        </>
      )}

      {o && k.karsilastirma && (
        <div data-bolum="karsilastirma">
          <AltBaslik>{cevir("Compared with OASIS")}</AltBaslik>
          {k.karsilastirma.ts && <p className="text-xs text-ink-3">{tarihSaat(k.karsilastirma.ts)}{k.kod && <> · {cevir("Reading job {0}", k.kod)}</>}</p>}
          {karsilastirmaSatirlari.length > 0 && (
            <ul className="mt-1 space-y-0.5 text-sm">
              {karsilastirmaSatirlari.map((n, i) => (
                <li key={i} className="flex gap-2">
                  <Nokta k={n.karsilastirildi ? "kesin" : n.suphe ? "suphe" : "bos"} />
                  <span className="min-w-0">{n.metin}</span>
                </li>
              ))}
            </ul>
          )}
          <AltBaslik>{cevir("Alerts from the reading")}</AltBaslik>
          {!k.karsilastirma.karsilastirildi && toplamUyari === 0
            ? <Band tip="not">{salt ? cevir("This reading was not compared with OASIS.") : cevir("This reading was not compared with OASIS. Use Compare again to compare it.")}</Band>
            : toplamUyari === 0
              ? <p className="text-sm text-ink-2">{cevir("No alerts from the comparison.")}</p>
              : (
                <div className="space-y-2">
                  {k.satirlar.filter((c) => c.uyarilar.length > 0).map((c) => {
                    const kalir = satinAlmaPoKalirMetni(k, c.sira);
                    return (
                      <div key={c.sira} data-satir-uyari={c.sira}>
                        <p className="text-xs font-medium text-ink-2">{cevir("Line {0}: {1}", c.sira, c.pn ?? "—")}</p>
                        <ul className="mt-0.5 space-y-1.5">
                          {c.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                        </ul>
                        {kalir && c.uyarilar.some((u) => u.kod === "TES-03") && <p className="mt-0.5 text-xs text-ink-3">{kalir}</p>}
                      </div>
                    );
                  })}
                  {k.karsilastirma.uyarilar.length > 0 && (
                    <ul className="space-y-1.5">
                      {k.karsilastirma.uyarilar.map((u) => <UyariSatiri key={u.id} u={u} yer={null} salt={salt} mesgul={mesgul} calistir={calistir} />)}
                    </ul>
                  )}
                </div>
              )}
          {k.karsilastirma.bulgular.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes from the reading")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-3">
                {k.karsilastirma.bulgular.map((b, i) => <li key={i}><span className="mr-1.5 font-mono">{b.kod}</span>{b.mesaj}</li>)}
              </ul>
            </>
          )}
          {degismedi && <p className="mt-1.5 text-sm text-ink-2">{cevir("No change: the comparison gives the same alerts.")}</p>}
          <p className="mt-2 flex flex-wrap gap-3 text-xs text-ink-3">
            <span className="inline-flex items-center gap-1"><Nokta k="kesin" />{cevir("certain")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="suphe" />{cevir("check")}</span>
            <span className="inline-flex items-center gap-1"><Nokta k="bos" />{cevir("not read")}</span>
          </p>
        </div>
      )}

      {(ey.gonder || ey.cikar || ey.girdi || ey.karsilastir) && (
        <div className="mt-2 flex flex-wrap gap-2">
          {ey.gonder && (
            <Buton kucuk tur="birincil" disabled={mesgul !== null} mesgul={mesgul === "ekle"} onClick={() => calistir("ekle", () => api.post(SATIN_ALMA_UCU.sira(satirId), { islem: "ekle" }))}>
              {cevir(okumaDugmesi(k))}
            </Buton>
          )}
          {ey.karsilastir && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "karsilastir"} onClick={() => calistir("karsilastir", async () => {
              const r = await api.post<{ ok: true; degisti: boolean }>(OKUMA_UCU.karsilastir(satirId));
              if (!r.degisti) setDegismedi(true);
            })}>
              {cevir("Compare again")}
            </Buton>
          )}
          {ey.cikar && (
            <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "cikar"} onClick={() => calistir("cikar", () => api.post(SATIN_ALMA_UCU.sira(satirId), { islem: "cikar" }))}>
              {cevir("Remove from reading queue")}
            </Buton>
          )}
          {ey.girdi && (
            <>
              <Buton kucuk disabled={mesgul !== null} mesgul={mesgul === "girdi"} onClick={() => secici.current?.click()}>{cevir("Upload the reading excel by hand")}</Buton>
              <input
                ref={secici}
                type="file"
                accept=".xlsx"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void calistir("girdi", () => dosyaYukle(SATIN_ALMA_UCU.girdi(satirId), f, {}, {}, "excel"));
                }}
              />
            </>
          )}
        </div>
      )}
      {hata && <Band tip="duvar" className="mt-2">{hata}</Band>}
    </div>
  );
}
